// The main menu's CODES screen (spec 2026-10-09): an Event-Archive-sized
// panel with a code box, SUBMIT to its right, and how many codes the player
// can still use under it. The box is a real DOM <input> laid over the canvas
// (Phaser has no text field), kept on the canvas's FIT-scaled rect.
import Phaser from 'phaser';
import { signedInUserId } from '../account/accountUi';
import { codesAvailable, codesClient, redeemCode } from '../account/codes';
import { drainDevInbox } from '../account/inbox';
import { CODE_NAME_MAX } from '../config/redeemCodes';

export const CODES_PURPLE = 0x7b3fc4;
export const CODES_PURPLE_HOVER = 0x9455de;
const CODES_LILAC_HEX = '#d9b8ff';

const PANEL_W = 820;
const PANEL_H = 250;
const D = 50;

/** Open the panel over `scene`; the returned function closes it. */
export function openCodesPanel(scene: Phaser.Scene): () => void {
  const W = 1280;
  const H = 720;
  const objs: Phaser.GameObjects.GameObject[] = [];
  const add = <T extends Phaser.GameObjects.GameObject>(o: T): T => {
    objs.push(o);
    return o;
  };
  let closed = false;

  add(
    scene.add
      .rectangle(W / 2, H / 2, W, H, 0x000000, 0.75)
      .setInteractive()
      .setDepth(D),
  );
  const panelX = (W - PANEL_W) / 2;
  const panelY = (H - PANEL_H) / 2;
  add(
    scene.add
      .rectangle(W / 2, panelY + PANEL_H / 2, PANEL_W, PANEL_H, 0x1a1026, 1)
      .setStrokeStyle(2, CODES_PURPLE)
      .setDepth(D + 1)
      .setName('codes-panel'),
  );
  add(
    scene.add
      .image(panelX + 42, panelY + 30, 'icon-gift')
      .setDisplaySize(32, 32)
      .setDepth(D + 2),
  );
  add(
    scene.add
      .text(panelX + 66, panelY + 30, 'CODES', {
        fontSize: '26px',
        color: CODES_LILAC_HEX,
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setLetterSpacing(2)
      .setDepth(D + 2),
  );
  add(
    scene.add
      .text(panelX + 26, panelY + 62, 'Type a code and press SUBMIT to claim its rewards.', {
        fontSize: '15px',
        color: '#b9a6cc',
        fontStyle: 'italic',
      })
      .setOrigin(0, 0.5)
      .setDepth(D + 2),
  );

  // The input's slot on the canvas (game coordinates).
  const box = { x: panelX + 26, y: panelY + 92, w: 560, h: 52 };
  add(
    scene.add
      .rectangle(box.x + box.w / 2, box.y + box.h / 2, box.w, box.h, 0x0d0814, 1)
      .setStrokeStyle(2, 0x5a3a80)
      .setDepth(D + 2),
  );

  // SUBMIT, to the right of the box.
  const sub = { x: box.x + box.w + 14, w: PANEL_W - 26 - (box.w + 14) - 26 };
  const subBg = add(scene.add.graphics().setDepth(D + 2));
  const drawSub = (hover: boolean, enabled: boolean) => {
    subBg.clear();
    subBg.fillStyle(enabled ? (hover ? CODES_PURPLE_HOVER : CODES_PURPLE) : 0x3d2f4d, 1);
    subBg.fillRoundedRect(sub.x, box.y, sub.w, box.h, 10);
    subBg.lineStyle(2, 0xffffff, enabled ? 0.85 : 0.3);
    subBg.strokeRoundedRect(sub.x, box.y, sub.w, box.h, 10);
  };
  add(
    scene.add
      .text(sub.x + sub.w / 2, box.y + box.h / 2, 'SUBMIT', {
        fontSize: '22px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(D + 3),
  );
  const subHit = add(
    scene.add
      .zone(sub.x + sub.w / 2, box.y + box.h / 2, sub.w, box.h)
      .setDepth(D + 4)
      .setName('codes-submit')
      .setInteractive({ useHandCursor: true }),
  );

  const counter = add(
    scene.add
      .text(box.x, box.y + box.h + 26, '', { fontSize: '18px', color: '#ffffff' })
      .setOrigin(0, 0.5)
      .setDepth(D + 2)
      .setName('codes-counter'),
  );
  const message = add(
    scene.add
      .text(box.x, box.y + box.h + 58, '', {
        fontSize: '17px',
        color: '#ff8a8a',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setDepth(D + 2)
      .setName('codes-message'),
  );

  const closeBtn = add(
    scene.add
      .text(panelX + PANEL_W - 30, panelY + 30, '✕', {
        fontSize: '26px',
        color: '#cfd3dc',
        padding: { x: 10, y: 4 },
      })
      .setOrigin(0.5)
      .setDepth(D + 2)
      .setInteractive({ useHandCursor: true })
      .setName('codes-close'),
  );
  closeBtn.on('pointerover', () => closeBtn.setColor(CODES_LILAC_HEX));
  closeBtn.on('pointerout', () => closeBtn.setColor('#cfd3dc'));

  // ── the DOM input ─────────────────────────────────────────────────────────
  const input = document.createElement('input');
  input.type = 'text';
  input.maxLength = CODE_NAME_MAX;
  input.autocomplete = 'off';
  input.spellcheck = false;
  input.placeholder = 'Enter code';
  input.dataset.testid = 'codes-input';
  input.setAttribute('aria-label', 'Code');
  Object.assign(input.style, {
    position: 'fixed',
    zIndex: '20',
    background: 'transparent',
    border: 'none',
    outline: 'none',
    color: '#ffffff',
    fontFamily: 'system-ui, sans-serif',
    fontWeight: '600',
    padding: '0 14px',
    boxSizing: 'border-box',
  } satisfies Partial<CSSStyleDeclaration>);
  // Phaser listens on the window: a click or a key in the box must not also
  // reach the game underneath.
  for (const ev of ['pointerdown', 'pointerup', 'mousedown', 'mouseup', 'touchstart', 'touchend']) {
    input.addEventListener(ev, (e) => e.stopPropagation());
  }
  input.addEventListener('keydown', (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') void submit();
    if (e.key === 'Escape') close();
  });
  input.addEventListener('keyup', (e) => e.stopPropagation());
  // Into the game's own container, so it still shows in fullscreen (only the
  // fullscreen element and its children are drawn then).
  (scene.game.canvas.parentElement ?? document.body).append(input);

  const place = () => {
    const rect = scene.game.canvas.getBoundingClientRect();
    const s = rect.width / W;
    input.style.left = `${rect.left + (box.x + 2) * s}px`;
    input.style.top = `${rect.top + (box.y + 2) * s}px`;
    input.style.width = `${(box.w - 4) * s}px`;
    input.style.height = `${(box.h - 4) * s}px`;
    input.style.fontSize = `${Math.max(12, 22 * s)}px`;
  };
  place();
  window.addEventListener('resize', place);
  scene.scale.on(Phaser.Scale.Events.RESIZE, place);

  // ── state ─────────────────────────────────────────────────────────────────
  const client = codesClient();
  const signedIn = client !== null && signedInUserId() !== null;
  let available: number | null = null;
  let busy = false;

  const showCount = () => {
    if (!signedIn) counter.setText('Sign in to redeem codes').setColor('#ffcf6b');
    else if (available === null) counter.setText('Codes available: …').setColor('#ffffff');
    else counter.setText(`Codes available: ${available}`).setColor('#ffffff');
  };
  const setEnabled = (on: boolean) => {
    input.disabled = !on;
    drawSub(false, on);
  };
  showCount();
  setEnabled(signedIn);
  if (signedIn) input.focus();

  if (client && signedIn) {
    void codesAvailable(client).then((r) => {
      if (closed) return;
      if (r.ok) available = r.value;
      showCount();
    });
  }

  async function submit(): Promise<void> {
    if (!client || !signedIn || busy) return;
    // Exact match (the brief): only the surrounding spaces are dropped.
    const code = input.value.trim();
    if (code === '') {
      message.setColor('#ff8a8a').setText('Type a code first.');
      return;
    }
    busy = true;
    setEnabled(false);
    message.setColor('#cfd3dc').setText('Checking…');
    const r = await redeemCode(client, code);
    busy = false;
    if (closed) return;
    setEnabled(true);
    if (!r.ok) {
      message.setColor('#ff8a8a').setText('Could not check the code — try again.');
      return;
    }
    if (r.value === 'unknown') {
      message.setColor('#ff8a8a').setText('That code is not valid.');
      return;
    }
    if (r.value === 'used') {
      message.setColor('#ff8a8a').setText('You have already used that code.');
      return;
    }
    input.value = '';
    message.setColor('#8ef0a0').setText('Code redeemed! Your rewards are on their way.');
    if (available !== null) available = Math.max(0, available - 1);
    showCount();
    // The rewards are a dev_inbox row: apply them now rather than wait on
    // Realtime. The banner at the top lists what landed.
    void drainDevInbox(client);
  }

  subHit.on('pointerover', () => drawSub(true, !input.disabled));
  subHit.on('pointerout', () => drawSub(false, !input.disabled));
  subHit.on('pointerup', () => void submit());

  function close(): void {
    if (closed) return;
    closed = true;
    window.removeEventListener('resize', place);
    scene.scale.off(Phaser.Scale.Events.RESIZE, place);
    input.remove();
    objs.forEach((o) => o.destroy());
  }
  closeBtn.on('pointerup', close);
  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, close);
  return close;
}
